/**
 * useSavedListings Hook
 *
 * Manages user's saved/favorited listings from Firestore.
 */

import { useState, useEffect, useCallback } from 'react';
import { db } from '../../../services/firebase';
import { collection, query, where, getDocs, deleteDoc, doc, orderBy, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../../contexts';

export interface SavedListing {
  id: string;
  title: string;
  propertyType: string;
  photo: string;
  location: {
    city: string;
    region: string;
  };
  pricing: {
    nightlyRate: number;
    currency: string;
  };
  rating?: number;
  reviewCount?: number;
  savedAt: Date;
}

export const useSavedListings = () => {
  const { user } = useAuth();
  const [listings, setListings] = useState<SavedListing[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSavedListings = async () => {
      if (!user) {
        setListings([]);
        setIsLoading(false);
        return;
      }

      try {
        const savedRef = collection(db, 'savedListings');
        const q = query(
          savedRef,
          where('userId', '==', user.uid),
          orderBy('savedAt', 'desc')
        );

        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          setListings([]);
          setIsLoading(false);
          return;
        }

        const savedData: SavedListing[] = snapshot.docs.map(docSnap => {
          const data = docSnap.data();
          return {
            id: data.listingId,
            title: data.listing?.title || 'Unknown',
            propertyType: data.listing?.propertyType || 'property',
            photo: data.listing?.photo || '',
            location: {
              city: data.listing?.location?.city || 'Lebanon',
              region: data.listing?.location?.region || '',
            },
            pricing: {
              nightlyRate: data.listing?.pricing?.nightlyRate || 0,
              currency: data.listing?.pricing?.currency || 'USD',
            },
            rating: data.listing?.rating,
            reviewCount: data.listing?.reviewCount,
            savedAt: data.savedAt?.toDate() || new Date(),
          };
        });

        setListings(savedData);
      } catch (error) {
        console.error('Error fetching saved listings:', error);
        setListings([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSavedListings();
  }, [user]);

  const removeSaved = useCallback(async (listingId: string) => {
    if (!user) return;

    try {
      // Find and delete the saved document
      const savedRef = collection(db, 'savedListings');
      const q = query(
        savedRef,
        where('userId', '==', user.uid),
        where('listingId', '==', listingId)
      );

      const snapshot = await getDocs(q);
      snapshot.docs.forEach(async (docSnap) => {
        await deleteDoc(doc(db, 'savedListings', docSnap.id));
      });

      setListings((prev) => prev.filter((l) => l.id !== listingId));
    } catch (error) {
      console.error('Error removing saved listing:', error);
    }
  }, [user]);

  const toggleSave = useCallback(async (listingId: string) => {
    if (!user) return;

    const isSaved = listings.some((l) => l.id === listingId);

    if (isSaved) {
      await removeSaved(listingId);
    } else {
      try {
        await addDoc(collection(db, 'savedListings'), {
          userId: user.uid,
          listingId,
          savedAt: serverTimestamp(),
        });
        // Optimistic update
        setListings((prev) => [
          { id: listingId, title: '', propertyType: '', photo: '', location: { city: '', region: '' }, pricing: { nightlyRate: 0, currency: 'USD' }, savedAt: new Date() },
          ...prev,
        ]);
      } catch (error) {
        console.error('Error saving listing:', error);
      }
    }
  }, [user, listings, removeSaved]);

  const savedIds = listings.map((l) => l.id);

  return { listings, isLoading, removeSaved, toggleSave, savedIds };
};

export default useSavedListings;
